import { ClockCircleOutlined, DollarOutlined, HomeOutlined, InfoCircleOutlined, TrophyOutlined, UserOutlined } from '@ant-design/icons';
import { Col, Progress, Row, Skeleton, Space, Tooltip, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import api from '../../../services/api';
import { glassCardStyle } from '../../Performance/utils';

const { Title, Text } = Typography;

// Helper functions for formatting (recreated locally to be self-contained)
const formatRupiah = (val: number) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(val);
};

const formatNumber = (val: number) => {
  return new Intl.NumberFormat('id-ID').format(val);
};

const getProgressColor = (percent: number) => {
  if (percent >= 90) return '#52c41a'; // Emerald Green
  if (percent >= 75) return '#1890ff'; // Blue
  if (percent >= 50) return '#faad14'; // Orange/Yellow
  return '#f5222d'; // Red
};

const MONTH_NAMES = [
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
];

interface DashboardPerformanceSummaryCardsProps {
  user: any;
  hasPermission: (permissionName: string) => boolean;
}

export const DashboardPerformanceSummaryCards: React.FC<DashboardPerformanceSummaryCardsProps> = ({
  user,
  hasPermission,
}) => {
  const [loading, setLoading] = useState(true);
  const [roleLabel, setRoleLabel] = useState('');
  const [data, setData] = useState<any>(null);

  const currentMonthIndex = new Date().getMonth(); // 0-11
  const currentMonthName = MONTH_NAMES[currentMonthIndex];
  const currentYear = new Date().getFullYear();

  // Identify roles based on our permission structure (Name-Based checking with precise role list)
  const isAdmin = user?.role?.name === 'admin';
  const isManagerHO = [
    'staff_marketingho',
    'vice_president',
    'marketing_managerho',
    'commercial_managerho',
    'staff_commercialho'
  ].includes(user?.role?.name) && !isAdmin;

  const isManagerKawasan = [
    'realty_manager',
    'staff_marketingunit',
    'marketing_sectionhead'
  ].includes(user?.role?.name) && !isAdmin;

  const isSalesAgent = [
    'sales_inhouse'
  ].includes(user?.role?.name) && !isAdmin;

  // Load appropriate data from real backend endpoints based on active role
  useEffect(() => {
    if (isAdmin) {
      setLoading(false);
      return;
    }

    const fetchRoleSummaryData = async () => {
      try {
        setLoading(true);
        let endpoint = '';
        if (isManagerHO) {
          setRoleLabel('Manager HO (Akumulatif Semua Kawasan)');
          endpoint = 'dashboard/performance/ho/summary';
        } else if (isManagerKawasan) {
          setRoleLabel('Manager Kawasan (Akumulatif Kawasan )');
          endpoint = 'dashboard/performance/kawasan/summary';
        } else if (isSalesAgent) {
          setRoleLabel('Sales Agent (Pencapaian Pribadi)');
          endpoint = 'dashboard/performance/agent/summary';
        }

        if (endpoint) {
          const response = await api.get(endpoint, {
            params: {
              periode: currentYear, // Automatically send the current year as required parameter
              month: currentMonthIndex + 1, // Automatically send the current month index (1-12) to get current month stats
            }
          });
          if (response.data?.status === 'success') {
            setData(response.data.data);
          } else {
            throw new Error('Failed to fetch status success from endpoint');
          }
        }
      } catch (error) {
        console.error('Failed to fetch role performance summary:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRoleSummaryData();
  }, [isManagerHO, isManagerKawasan, isSalesAgent, isAdmin, currentMonthIndex, currentYear]);

  // Render nothing for Admin (placed above the loading check so the skeleton does not show for admins)
  if (isAdmin) {
    return null;
  }

  if (loading || !data) {
    return (
      <div style={{ marginBottom: '24px' }}>
        <div style={{ marginBottom: '12px' }}>
          <Skeleton.Button active style={{ width: 200, height: 20 }} />
        </div>
        <Row gutter={[20, 20]}>
          {[1, 2, 3, 4].map(idx => (
            <Col key={idx} xs={24} sm={12} lg={6}>
              <div style={glassCardStyle}>
                <Skeleton active paragraph={{ rows: 2 }} title={false} />
              </div>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '24px' }}>
      {/* Role Badge Indicator */}
      <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', padding: '0 4px', flexWrap: 'wrap' }}>
        <TrophyOutlined style={{ color: '#7BAA18', fontSize: '16px' }} />
        <Text strong style={{ color: '#475569', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Rapor Kinerja: <span style={{ color: '#7BAA18', fontWeight: 800 }}>{roleLabel}</span>
        </Text>
        <span style={{
          background: 'rgba(123, 170, 24, 0.15)',
          color: '#7BAA18',
          padding: '2px 8px',
          borderRadius: '12px',
          fontSize: '11px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          Bulan {currentMonthName} {currentYear}
        </span>
      </div>

      <Row gutter={[20, 20]}>
        {/* KPI 1: Realisasi Omzet */}
        <Col xs={24} sm={12} lg={6}>
          <div style={glassCardStyle} className="glass-card-interactive">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div>
                <Space size={6}>
                  <Text type="secondary" strong style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Realisasi Omzet
                  </Text>
                  <Tooltip title={`Nilai transaksi pemesanan unit (closing) yang sudah disetujui (Approved) secara resmi oleh Kantor Pusat pada bulan ${currentMonthName} ${currentYear}. Transaksi pending atau rejected tidak dihitung.`}>
                    <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '12px', cursor: 'pointer' }} />
                  </Tooltip>
                </Space>
                <Title level={3} style={{ margin: '4px 0 0 0', color: '#0f172a', fontWeight: 800 }}>
                  {formatRupiah(data.omzet.realization)}
                </Title>
              </div>
              <div style={{ padding: '10px', background: 'rgba(123, 170, 24, 0.15)', borderRadius: '10px' }}>
                <DollarOutlined style={{ fontSize: '22px', color: '#7BAA18' }} />
              </div>
            </div>
            <div style={{ marginTop: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>Target: {formatRupiah(data.omzet.target)}</Text>
                <Text strong style={{ color: getProgressColor(data.omzet.percentage), fontSize: '12px' }}>
                  {data.omzet.percentage}%
                </Text>
              </div>
              <Progress
                percent={data.omzet.percentage}
                showInfo={false}
                strokeColor={getProgressColor(data.omzet.percentage)}
                status={data.omzet.percentage >= 100 ? "success" : "normal"}
                strokeWidth={6}
              />
            </div>
          </div>
        </Col>

        {/* KPI 2: Unit Terjual */}
        <Col xs={24} sm={12} lg={6}>
          <div style={glassCardStyle} className="glass-card-interactive">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div>
                <Space size={6}>
                  <Text type="secondary" strong style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Unit Terjual
                  </Text>
                  <Tooltip title={`Jumlah unit rumah/properti yang telah sukses terjual (approved oleh manajemen Ho) pada bulan ${currentMonthName} ${currentYear}.`}>
                    <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '12px', cursor: 'pointer' }} />
                  </Tooltip>
                </Space>
                <Title level={3} style={{ margin: '4px 0 0 0', color: '#0f172a', fontWeight: 800 }}>
                  {formatNumber(data.unit.realization)} <span style={{ fontSize: '13px', fontWeight: 'normal', color: '#64748b' }}>Unit</span>
                </Title>
              </div>
              <div style={{ padding: '10px', background: 'rgba(24, 144, 255, 0.15)', borderRadius: '10px' }}>
                <HomeOutlined style={{ fontSize: '22px', color: '#1890ff' }} />
              </div>
            </div>
            <div style={{ marginTop: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>Target: {formatNumber(data.unit.target)} Unit</Text>
                <Text strong style={{ color: getProgressColor(data.unit.percentage), fontSize: '12px' }}>
                  {data.unit.percentage}%
                </Text>
              </div>
              <Progress
                percent={data.unit.percentage}
                showInfo={false}
                strokeColor={getProgressColor(data.unit.percentage)}
                strokeWidth={6}
              />
            </div>
          </div>
        </Col>

        {/* KPI 3: Pencapaian Lead */}
        <Col xs={24} sm={12} lg={6}>
          <div style={glassCardStyle} className="glass-card-interactive">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div>
                <Space size={6}>
                  <Text type="secondary" strong style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Pencapaian Lead
                  </Text>
                  <Tooltip title={`Realisasi jumlah calon pembeli (leads) baru yang berhasil diproses dibandingkan target leads pada bulan ${currentMonthName} ${currentYear}.`}>
                    <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '12px', cursor: 'pointer' }} />
                  </Tooltip>
                </Space>
                <Title level={3} style={{ margin: '4px 0 0 0', color: '#0f172a', fontWeight: 800 }}>
                  {formatNumber(data.leads.realization)} <span style={{ fontSize: '13px', fontWeight: 'normal', color: '#64748b' }}>Leads</span>
                </Title>
              </div>
              <div style={{ padding: '10px', background: 'rgba(250, 173, 20, 0.15)', borderRadius: '10px' }}>
                <UserOutlined style={{ fontSize: '22px', color: '#faad14' }} />
              </div>
            </div>
            <div style={{ marginTop: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>Target: {formatNumber(data.leads.target)} Leads</Text>
                <Text strong style={{ color: getProgressColor(data.leads.percentage), fontSize: '12px' }}>
                  {data.leads.percentage}%
                </Text>
              </div>
              <Progress
                percent={data.leads.percentage}
                showInfo={false}
                strokeColor={getProgressColor(data.leads.percentage)}
                strokeWidth={6}
              />
            </div>
          </div>
        </Col>

        {/* KPI 4: On-Time SLA */}
        <Col xs={24} sm={12} lg={6}>
          <div style={glassCardStyle} className="glass-card-interactive">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div>
                <Space size={6}>
                  <Text type="secondary" strong style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    On-Time SLA
                  </Text>
                  <Tooltip title={`Tingkat kepatuhan dalam menindaklanjuti leads tepat waktu sesuai SLA pada bulan ${currentMonthName} ${currentYear}. Standar kepatuhan tim adalah 85%.`}>
                    <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '12px', cursor: 'pointer' }} />
                  </Tooltip>
                </Space>
                <Title level={3} style={{ margin: '4px 0 0 0', color: '#0f172a', fontWeight: 800 }}>
                  {data.sla.compliance_rate}%
                </Title>
              </div>
              <div style={{ padding: '10px', background: 'rgba(255, 77, 79, 0.15)', borderRadius: '10px' }}>
                <ClockCircleOutlined style={{ fontSize: '22px', color: '#ff4d4f' }} />
              </div>
            </div>
            <div style={{ marginTop: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>Standar Tim: 85%</Text>
                <Text strong style={{ color: getProgressColor(data.sla.compliance_rate), fontSize: '12px' }}>
                  {data.sla.compliance_rate >= 85 ? 'Sangat Bagus' : 'Butuh Evaluasi'}
                </Text>
              </div>
              <Progress
                percent={data.sla.compliance_rate}
                showInfo={false}
                strokeColor={getProgressColor(data.sla.compliance_rate)}
                strokeWidth={6}
              />
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardPerformanceSummaryCards;
